import React, { useState, useEffect } from 'react';
import ApiClient from '../api/client';
import { AlertIcon } from './icons';

interface ResponseConfigEditorProps {
  formId: number;
  apiClient: ApiClient;
  responseMode: string;
  responseConfig: Record<string, any> | null;
  onUpdated?: () => void;
}

const ResponseConfigEditor: React.FC<ResponseConfigEditorProps> = ({
  formId,
  apiClient,
  responseMode: initialMode,
  responseConfig: initialConfig,
  onUpdated,
}) => {
  const [mode, setMode] = useState<'json' | 'redirect' | 'custom_json'>(
    (initialMode as 'json' | 'redirect' | 'custom_json') || 'json'
  );
  const [redirectUrl, setRedirectUrl] = useState('');
  const [customPayload, setCustomPayload] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    setMode((initialMode as 'json' | 'redirect' | 'custom_json') || 'json');
    if (initialConfig) {
      if (initialConfig.redirectUrl) setRedirectUrl(initialConfig.redirectUrl);
      if (initialConfig.payload) setCustomPayload(JSON.stringify(initialConfig.payload, null, 2));
    }
  }, [initialMode, initialConfig]);

  const handleModeChange = (newMode: 'json' | 'redirect' | 'custom_json') => {
    setMode(newMode);
    setIsDirty(true);
    setError(null);
  };

  const handleSave = async () => {
    let config: Record<string, any> | null = null;

    if (mode === 'redirect') {
      if (!redirectUrl.trim()) {
        setError('Redirect URL is required');
        return;
      }
      config = { redirectUrl: redirectUrl.trim() };
    } else if (mode === 'custom_json') {
      if (customPayload.trim()) {
        try {
          config = { payload: JSON.parse(customPayload) };
        } catch {
          setError('Invalid JSON in custom payload');
          return;
        }
      }
    }

    setSaving(true);
    try {
      await apiClient.updateForm(formId, {
        responseMode: mode,
        responseConfig: config,
      });
      setIsDirty(false);
      setError(null);
      onUpdated?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save response config');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="details-section">
      <h4>Submission Response</h4>

      {error && (
        <div className="alert alert-error">
          <AlertIcon size={15} />
          <span>{error}</span>
        </div>
      )}

      <div className="editor-intro">
        <p>
          Configure what happens after a form is submitted. The response is returned to the browser immediately.
        </p>
      </div>

      <div className="form-group">
        <label>Response Mode</label>
        <select
          value={mode}
          onChange={(e) => handleModeChange(e.target.value as 'json' | 'redirect' | 'custom_json')}
          disabled={saving}
        >
          <option value="json">JSON (default) — returns {"{"}&quot;status&quot;: &quot;received&quot;{"}"}</option>
          <option value="redirect">Redirect — send user to a URL after submission</option>
          <option value="custom_json">Custom JSON — return a custom payload with template variables</option>
        </select>
      </div>

      {mode === 'redirect' && (
        <div className="form-group">
          <label>Redirect URL</label>
          <input
            type="text"
            value={redirectUrl}
            onChange={(e) => { setRedirectUrl(e.target.value); setIsDirty(true); }}
            placeholder="https://example.com/thank-you"
            disabled={saving}
          />
          <small>
            Supports template variables: {'{{submissionId}}'}, {'{{formHash}}'}, or any form field like {'{{name}}'}
          </small>
        </div>
      )}

      {mode === 'custom_json' && (
        <div className="form-group">
          <label>Custom JSON Payload</label>
          <textarea
            value={customPayload}
            onChange={(e) => { setCustomPayload(e.target.value); setIsDirty(true); }}
            placeholder={'{\n  "message": "Thanks {{name}}!",\n  "id": "{{submissionId}}"\n}'}
            disabled={saving}
            className="mono-input"
            style={{ minHeight: '120px' }}
          />
          <small>
            Template variables: {'{{submissionId}}'}, {'{{formHash}}'}, {'{{status}}'}, or any form field name like {'{{name}}'}, {'{{email}}'}
          </small>
        </div>
      )}

      {isDirty && (
        <div className="editor-actions">
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn btn-primary editor-save"
          >
            {saving ? 'Saving...' : 'Save Response Config'}
          </button>
          <button
            onClick={() => {
              setMode((initialMode as 'json' | 'redirect' | 'custom_json') || 'json');
              setRedirectUrl(initialConfig?.redirectUrl || '');
              setCustomPayload(initialConfig?.payload ? JSON.stringify(initialConfig.payload, null, 2) : '');
              setIsDirty(false);
              setError(null);
            }}
            disabled={saving}
            className="btn btn-secondary"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

export default ResponseConfigEditor;
